import { useEffect, useState } from 'react';
import PageShell from '@/src/Common/components/PageShell';
import { Button } from '@/src/shadComponents/ui/button';
import { Maximize, Minimize, Monitor, Settings } from 'lucide-react';
import useCommandCenter from './useCommandCenter';
import CommandCenterCard from './CommandCenterCard';
import ActivationPickerDialog from './ActivationPickerDialog';
import useActivations from '@/src/Activations/components/hooks/useActivations';

interface ActivationInfo {
    id: number;
    name: string;
}

function CardGrid({
    selectedIds,
    mediaMap,
    activationNames,
    removeActivation,
    fullScreen,
}: {
    selectedIds: number[];
    mediaMap: ReturnType<typeof useCommandCenter>['mediaMap'];
    activationNames: Record<number, string>;
    removeActivation: (id: number) => void;
    fullScreen: boolean;
}) {
    return (
        <div
            className={
                fullScreen
                    ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4'
                    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
            }
        >
            {selectedIds.map((id) => {
                const media = mediaMap[id];
                return (
                    <CommandCenterCard
                        key={id}
                        activationId={id}
                        activationName={
                            activationNames[id] || `Activation ${id}`
                        }
                        photo={media?.photo ?? null}
                        photoCount={media?.photoCount ?? 0}
                        isLoading={media?.isLoading ?? true}
                        onRemove={removeActivation}
                    />
                );
            })}
        </div>
    );
}

export default function CommandCenter() {
    const { selectedIds, updateSelectedIds, removeActivation, mediaMap } =
        useCommandCenter();
    const { getActivations } = useActivations();
    const [pickerOpen, setPickerOpen] = useState(false);
    const [fullScreen, setFullScreen] = useState(false);
    const [activationNames, setActivationNames] = useState<
        Record<number, string>
    >({});

    // Fetch activation names for selected IDs
    useEffect(() => {
        if (selectedIds.length === 0) return;

        let isMounted = true;

        const fetch = async () => {
            try {
                const all: ActivationInfo[] = await getActivations();
                if (!isMounted) return;
                const names: Record<number, string> = {};
                for (const a of all) {
                    if (selectedIds.includes(a.id)) {
                        names[a.id] = a.name;
                    }
                }
                setActivationNames(names);
            } catch (err) {
                console.error('Failed to fetch activations', err);
            }
        };

        fetch();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds]);

    // ESC to exit full screen
    useEffect(() => {
        if (!fullScreen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullScreen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [fullScreen]);

    if (fullScreen) {
        return (
            <>
                <div className="fixed inset-0 z-50 bg-background overflow-auto">
                    <header className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-6 py-4">
                        <div>
                            <h1 className="text-xl font-bold tracking-tight text-foreground">
                                Command Center
                            </h1>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Press ESC to exit full screen
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPickerOpen(true)}
                            >
                                <Settings className="size-4 mr-2" />
                                Settings
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setFullScreen(false)}
                            >
                                <Minimize className="size-4 mr-2" />
                                Exit
                            </Button>
                        </div>
                    </header>
                    <div className="p-6">
                        {selectedIds.length === 0 ? (
                            <div className="text-center py-20">
                                <Monitor className="size-12 mx-auto mb-4 text-muted-foreground opacity-40" />
                                <h2 className="text-lg font-semibold text-foreground mb-2">
                                    No activations selected
                                </h2>
                                <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                                    Select activations to monitor their latest
                                    uploads in real-time with auto-refreshing
                                    previews.
                                </p>
                                <Button onClick={() => setPickerOpen(true)}>
                                    <Settings className="size-4 mr-2" />
                                    Select Activations
                                </Button>
                            </div>
                        ) : (
                            <CardGrid
                                selectedIds={selectedIds}
                                mediaMap={mediaMap}
                                activationNames={activationNames}
                                removeActivation={removeActivation}
                                fullScreen
                            />
                        )}
                    </div>
                </div>

                <ActivationPickerDialog
                    open={pickerOpen}
                    onOpenChange={setPickerOpen}
                    selectedIds={selectedIds}
                    onSave={updateSelectedIds}
                />
            </>
        );
    }

    return (
        <PageShell
            title="Command Center"
            subtitle="Monitor your activations in real-time."
            breadcrumbs={[
                { label: 'Dashboard', href: '/home' },
                { label: 'Command Center' },
            ]}
            actions={
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFullScreen(true)}
                    >
                        <Maximize className="size-4 mr-2" />
                        Full Screen
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPickerOpen(true)}
                    >
                        <Settings className="size-4 mr-2" />
                        Settings
                    </Button>
                </div>
            }
        >
            <div className="px-4 sm:px-6 lg:px-8 pb-8">
                {selectedIds.length === 0 ? (
                    <div className="text-center py-20">
                        <Monitor className="size-12 mx-auto mb-4 text-muted-foreground opacity-40" />
                        <h2 className="text-lg font-semibold text-foreground mb-2">
                            No activations selected
                        </h2>
                        <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                            Select activations to monitor their latest uploads
                            in real-time with auto-refreshing previews.
                        </p>
                        <Button onClick={() => setPickerOpen(true)}>
                            <Settings className="size-4 mr-2" />
                            Select Activations
                        </Button>
                    </div>
                ) : (
                    <CardGrid
                        selectedIds={selectedIds}
                        mediaMap={mediaMap}
                        activationNames={activationNames}
                        removeActivation={removeActivation}
                        fullScreen={false}
                    />
                )}
            </div>

            <ActivationPickerDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                selectedIds={selectedIds}
                onSave={updateSelectedIds}
            />
        </PageShell>
    );
}
